import { html, TemplateResult } from "lit-html";
import {
  convertOverviewFlightState,
  convertTime,
  renderSplitStringArray,
  sanitizeDateString,
  validateBaggageState,
  renderTerminalHighlight,
} from "Helper/flightInformationHelper";
import { getDocLocale } from "Helper/metaHelper";
import { replacePlaceHolder } from "Helper/stringHelper";
import { I18nService } from "Services/I18nService";
import { FlightsInformation } from "Interfaces/flightsInformation";
import FraMFlights from "Components/fra-m-flights/fra-m-flights";

/** ####### HELPER ####### */

/**
 * Helper to check for cancelled status
 * @param {string} status
 * @returns {boolean}
 */
function isCancelled(status: string): boolean {
  return (
    Boolean(status) &&
    (status.toLowerCase() === "annulliert" ||
      status.toLowerCase() === "cancelled" ||
      status.toLocaleLowerCase() === "取消")
  );
}

/**
 * Helper to check for Train status
 * @param {string} ac
 * @returns {boolean}
 */
function hasTrain(ac: string): boolean {
  return Boolean(ac) && ac === "TRS";
}

function getTrainLabel(track: string): string {
  return replacePlaceHolder(
    I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.row.track"),
    track.substring(1),
  );
}

/**
 * Template for flight table
 *
 * Renders JSON data from API w/ lit-html
 */

/** ####### RENDERER ####### */

/**
 * Render times associated w/ this flight (planned and delayed)
 * @param {string} sched
 * @param {string | undefined} esti
 * @returns {TemplateResult}
 */
function renderTime(sched: string, esti?: string): TemplateResult {
  if (!sched) {
    return html``;
  }
  if (esti) {
    return html`
      <span class="fra-m-flights__td-time-delayed"><time value="${sched}">${convertTime(sched)}</time></span>
      <span class="fra-m-flights__td-updated-info"><time value="${esti}">${convertTime(esti)}</time></span>
    `;
  }
  return html`
    <time value="${sched}">${convertTime(sched)}</time>
  `;
}

/**
 * Render destination airport and layover if available
 * @param {string} apname
 * @param {string | undefined} rouname layover, if available
 * @param {string | undefined} div redirected, if available
 * @param {string | undefined} divnam redirected airport name, if available
 * @returns {TemplateResult}
 */
function renderDestination(apname: string, rouname?: string, div?: string, divname?: string): TemplateResult {
  if (!apname) {
    return html``;
  }

  if (div) {
    return html`
      <div class="fra-m-flights__td-flight-redirected-old">${apname}</div>
      <div class="fra-m-flights__td-flight-redirected">${divname}</div>
    `;
  }

  if (rouname) {
    return html`
      ${apname}, ${rouname}
    `;
  }
  return html`
    ${apname}
  `;
}

/**
 * Render link to flight detail
 *
 * @param {string} id *
 * @param {String} flightDetailsUrl
 * @param {FraMFlights} component
 * @returns {TemplateResult}
 */
function renderLink(id: string, flightDetailsUrl: string, component: FraMFlights): TemplateResult {
  if (!id && !flightDetailsUrl) {
    return html``;
  }

  return html`
    <a
      class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right"
      data-gaTracking='{"event":"gaEvent","eventCategory":"FlightOverview","eventAction":"Selection", "eventLabel": "${id}"}'
      href="${flightDetailsUrl}/${id}.html"
      @click="${() => component.handleFlightDetailClick()})}"
      target="_self"
    >
      <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right"></fra-e-icon>
    </a>
  `;
}

/**
 * Wrap the content of a td with a span for highlighting purpose
 * @param {FlightsInformation} data
 * @param {boolean} isStatusCol
 * @returns {TemplateResult}
 */
function renderHighlight(data: FlightsInformation, isStatusCol?: boolean): TemplateResult {
  const flstatus: number = data.flstatus;
  const status: string = data.status;
  const bag: string[] = data.bag;

  if (isStatusCol && status) {
    return html`
      <div class="fra-m-flights__highlight--${convertOverviewFlightState(flstatus, status)}">
        ${validateBaggageState(status)
          ? html`
              <p class="fra-m-flights__highlight fra-p--small">
                <fra-e-icon class="fra-e-icon" icon-id="fra-icon-gepaeck-klein"></fra-e-icon>
                <span class="fra-m-flights__highlight--content">${status}</span>
              </p>
              ${bag
                ? html`
                    <p class="fra-m-flights__highlight--bag fra-p--small">
                      ${replacePlaceHolder(
                        I18nService.getInstance().getKey(
                          "fraport.travel.components.content.flight.table.row.status.band.label",
                        ),
                        bag.join(", "),
                      )}
                    </p>
                  `
                : html``}
            `
          : html`
              <p class="fra-m-flights__highlight fra-p--small">
                <span class="fra-m-flights__highlight--indicator"></span>
                <span class="fra-m-flights__highlight--content">${status}</span>
              </p>
            `}
      </div>
    `;
  }

  return html`
    <p class="fra-m-flights__highlight fra-m-flights__highlight--grey">&mdash;</p>
  `;
}

/**
 * Check whether a date row needs to be inserted by comparing flight dates
 * @param {string} newDate - date of current flight in data set
 * @param {string} oldDate - date of previous flight in data set
 * @returns {boolean}
 */
function checkDate(newDate: string, oldDate: string): boolean {
  const newDateStr = newDate.split("T")[0];
  const oldDateStr = oldDate.split("T")[0];
  return newDateStr === oldDateStr;
}

function renderGate(gate?: string, status?: string, oldgate?: string, isTrain = false, pos): TemplateResult {
  if (status && isCancelled(status)) {
    return html``;
  }

  if (gate && oldgate) {
    gate = isTrain ? getTrainLabel(gate) : gate;
    oldgate = isTrain ? getTrainLabel(oldgate) : oldgate;

    return html`
      <div class="fra-m-flights__td-flight-old-gate">${oldgate}</div>
      <div class="fra-m-flights__td-flight-new-gate">${gate}</div>
    `;
  }

  if (gate) {
    gate = isTrain ? getTrainLabel(gate) : gate;

    return html`
      ${gate}
    `;
  }

  if (pos) {
    gate = isTrain ? getTrainLabel(pos) : pos;

    return html`
      ${gate}
    `;
  }

  return html`
    &mdash;
  `;
}

function renderTerminal(terminal: string, status: string, ac: string, oldterminal?: string): TemplateResult {
  if (!terminal || isCancelled(status)) {
    return html``;
  }

  const trainTerminal = hasTrain(ac)
    ? html`
        <div
          class="fra-m-flights__td-terminal-content fra-m-flights__td-terminal-train  fra-m-flights__td-terminal--grey"
        >
          <fra-e-icon class="fra-e-icon" icon-id="fra-icon-zug-klein"></fra-e-icon>
        </div>
      `
    : html``;

  if (terminal && oldterminal) {
    return html`
      <div class="fra-m-flights__td-terminal-content-redirected">
        <div
          class="fra-m-flights__td-terminal-content fra-m-flights__td-terminal--${renderTerminalHighlight(
            oldterminal,
          )}-old fra-m-flights__td-old-terminal"
        >
          <span class="fra-m-flights__td-terminal-content-text">${oldterminal}</span>
        </div>
        <div
          class="fra-m-flights__td-terminal-content fra-m-flights__td-terminal--${renderTerminalHighlight(terminal)}"
        >
          <span class="fra-m-flights__td-terminal-content-text">${terminal}</span>
        </div>
      </div>
      ${trainTerminal}
    `;
  }

  return html`
    <div class="fra-m-flights__td-terminal-content fra-m-flights__td-terminal--${renderTerminalHighlight(terminal)}">
      <span class="fra-m-flights__td-terminal-content-text">${terminal}</span>
    </div>
    ${trainTerminal}
  `;
}

/*
 * Render zone associated w/ Halle
 * @param {string} halle, @param {string} status,  @param {string} oldhall
 * @returns {TemplateResult}
 */
function renderCheckIn(
  halle?: string,
  status?: string,
  oldhall?: string,
  schalter?: string,
  isArrival = false,
): TemplateResult {
  if (status && isCancelled(status)) {
    return html``;
  }

  if (schalter) {
    halle += `, ${schalter}`;
  }

  let label = I18nService.getInstance().getKey(
    "fraport.travel.components.content.flight.table.row.location.checkin.hall",
  );

  // on arrival page "halle" is not used
  if (isArrival) {
    label = I18nService.getInstance().getKey(
      "fraport.travel.components.content.flight.table.row.location.checkin.arrival",
    );
  }

  if (oldhall && halle) {
    return html`
      <div class="fra-m-flights__td-flight-old-hall">${replacePlaceHolder(label, oldhall)}</div>
      <div class="fra-m-flights__td-flight-new-hall">${replacePlaceHolder(label, halle)}</div>
    `;
  }

  if (halle) {
    return html`
      ${replacePlaceHolder(label, halle)}
    `;
  }
  return html`
    &mdash;
  `;
}

/**
 * Render code shares associated w/ status
 * @param {Array|undefined} arr
 * @returns {TemplateResult}
 */
function renderCodes(arr?: string[]): TemplateResult {
  if (!arr) {
    return html``;
  }

  const codesArr: string[] = renderSplitStringArray(arr);
  if (codesArr.length > 1) {
    const codes = codesArr.length;
    return html`
      <ul
        class="fra-m-flights__td-codes__slides"
        style="--items: ${Number(codes)}; 
        -webkit-animation-duration: ${Number(codes * 3)}s;
        -webkit-animation-timing-function: steps(${Number(codes)});
        animation-duration: ${Number(codes * 3)}s;
        animation-timing-function: steps(${Number(codes)});"
      >
        ${codesArr.map(
          (code: string) => html`
            <li class="fra-m-flights__td-codes__slide-up">${code}</li>
          `,
        )}
        <li class="fra-m-flights__td-codes__slide-up">${codesArr[0]}</li>
      </ul>
    `;
  }
  return html`
    ${codesArr[0]}
  `;
}

/** ####### EXPORTS ####### */

/**
 * Render a single row of flight data
 *
 * @param {FlightsInformation} row
 * @param {String} flightDetailsUrl
 * @param {FraMFlights} component
 * @returns {TemplateResult}
 */
export const flightsRowTemplate = (
  row: FlightsInformation,
  flightDetailsUrl: string,
  component: FraMFlights,
): TemplateResult => {
  return html`
    <tr
      class="fra-m-flights__row ${isCancelled(row.status) ? "fra-m-flights__cancelled" : ""} ${hasTrain(row.ac)
        ? "fra-m-flights__td-content-train"
        : ""}"
    >
      <td class="fra-m-flights__td-time">${renderTime(row.sched, row.esti)}</td>
      <td class="fra-m-flights__td-flight-info">
        <div class="fra-m-flights__td-destination">
          ${renderDestination(row.apname, row.rouname, row.div, row.divname)}
        </div>
        <div class="fra-m-flights__td-flight">${row.fnr || ""}</div>
        <div class="fra-m-flights__td-codes ${row.cs?.length ? "" : "fra-m-flights__td-codes-null"}">
          ${renderCodes(row.cs)}
        </div>
      </td>
      <td class="fra-m-flights__td-terminal">${renderTerminal(row.terminal, row.status, row.ac, row.oldterminal)}</td>
      <td class="fra-m-flights__td-check-in">
        ${row.schedArr
          ? renderCheckIn(row.halle, row.status, row.oldhall, row.schalter)
          : renderCheckIn(row.halle, row.status, row.oldhall, row.schalter, true)}
      </td>
      <td class="fra-m-flights__td-gate">
        ${renderGate(row.gate, row.status, row.oldgate, hasTrain(row.ac), row.pos)}
      </td>
      <td class="fra-m-flights__td-status">${renderHighlight(row, true)}</td>
      <td class="fra-m-flights__td-link">${renderLink(row.id, flightDetailsUrl, component)}</td>
    </tr>
  `;
};

export const dateRowTemplate = (date: string): TemplateResult => {
  const options: Intl.DateTimeFormatOptions = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
  // prepare date for parsing
  const prepDate = sanitizeDateString(date);
  const realDate = new Date(prepDate);
  const loc = getDocLocale();
  const dateStr = realDate.toLocaleDateString(loc, options);
  return html`
    <tr>
      <td class="fra-m-flights__td-date" colspan="8">
        <time class="fra-m-flights__td-date-time" value="${prepDate}">${dateStr}</time>
      </td>
    </tr>
  `;
};

export const errorMessageTemplate = (message: string): TemplateResult => {
  return html`
    <fra-e-icon class="fra-e-icon" icon-id="fra-icon-achtung-filled"></fra-e-icon>
    <span class="fra-m-flights-search__error--message">${message}</span>
  `;
};

export const noResultsMessageTemplate = (message: string): TemplateResult => {
  return html`
    <span class="fra-m-flights__no-results--message">${message}</span>
  `;
};

/**
 * Render multiple data-sets of flights in rows
 *
 * @param {Array<FlightsInformation>} data Collected flight data in array
 * @param {String} flightDetailsUrl
 * @param {String} trainDetailsUrl
 * @param {FraMFlights} component
 * @returns {TemplateResult}
 */
export const flightsTemplate = (
  data: Array<FlightsInformation>,
  flightDetailsUrl: string,
  trainDetailsUrl: string,
  component: FraMFlights,
): TemplateResult => {
  if (!data?.length) return html``;
  const result = new Array(data.length);
  let prevSched = "";

  data.forEach(function (row) {
    if (!checkDate(row.sched, prevSched)) {
      prevSched = row.sched;
      result.push(dateRowTemplate(row.sched));
    }
    result.push(flightsRowTemplate(row, row.ac === "TRS" ? trainDetailsUrl : flightDetailsUrl, component));
  });

  return html`
    ${result}
  `;
};
